"use client";

/**
 * WrappedAudio — gesture-gated ambient audio loop for the Niche v3 Wrapped overlay.
 *
 * WHY this exists: dream-selling references (Spotify Wrapped, Apple Replay) lean
 * heavily on a short looping ambient track. Gen Z reads "Wrapped" UX as
 * "music starts the moment I open it". Without audio, the finale feels flat.
 *
 * Browser autoplay policy: Chrome/Safari refuse to play audio before the user
 * has gestured on the document. WrappedFinale opens via a button click, but
 * mounting `<audio autoplay>` is not enough on its own — we wait for the user
 * to interact with the Wrapped surface (tap to advance / tap the mute button)
 * before we ever call `.play()`.
 *
 * File-missing fallback: assets live under `public/audio/niche/mood-{mood}.mp3`.
 * If the file is absent (404 → `error` event on `<audio>`) we silently hide the
 * UI control. The overlay keeps working, no error toast, no blank button.
 *
 * Accessibility:
 *   - `prefers-reduced-motion` → audio stays muted by default. User must explicitly
 *     tap the speaker icon to enable. No surprise sound for sensitive users.
 *   - Keyboard: `M` toggles mute (registered globally while `active`). Documented
 *     via `aria-keyshortcuts="m"` on the button.
 *   - Screen reader: live region announces "Ambient audio on/off" on every toggle.
 *   - Button has explicit `aria-label` reflecting current state.
 *
 * Lifecycle:
 *   - `active=false`           → pause + return (no DOM mount of <audio> button).
 *   - `active=true, no gesture`→ <audio> mounted, paused, button shows VolumeX.
 *   - First tap on button      → set userAllowedPlay=true → effect kicks .play().
 *   - Subsequent taps          → toggle muted only.
 *   - Component unmount        → audio pauses (audioRef cleanup).
 */

import { useCallback, useEffect, useRef, useState } from "react";
import { Volume2, VolumeX } from "lucide-react";
import { useReducedMotion } from "framer-motion";

interface WrappedAudioProps {
  /** Mood signal from `niche.visualGradient.mood` — picks which file to play. */
  mood: "hot" | "stable" | "frontier";
  /** Whether the Wrapped overlay is currently open / visible. */
  active: boolean;
  /** Volume 0..1 (default 0.35 — sits quietly under VO/visuals). */
  volume?: number;
  /** Optional analytics hook fired on every toggle. */
  onToggle?: (next: { muted: boolean; userAllowedPlay: boolean }) => void;
}

export function WrappedAudio({
  mood,
  active,
  volume = 0.35,
  onToggle,
}: WrappedAudioProps) {
  const reduced = useReducedMotion();
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const [muted, setMuted] = useState(false);
  const [userAllowedPlay, setUserAllowedPlay] = useState(false);
  const [fileMissing, setFileMissing] = useState(false);
  const [announce, setAnnounce] = useState<string>("");

  /** When the overlay closes / mood swaps / user mutes — pause and bail. */
  useEffect(() => {
    const el = audioRef.current;
    if (!el) return;
    if (!active || muted || !userAllowedPlay || fileMissing) {
      el.pause();
      return;
    }
    el.volume = Math.max(0, Math.min(1, volume));
    void el.play().catch(() => {
      // Autoplay blocked again (rare race) — flip back so the next gesture can retry.
      setUserAllowedPlay(false);
    });
  }, [active, muted, userAllowedPlay, fileMissing, volume, mood]);

  /** Cleanup on unmount: ensure audio is paused, ref is dropped. */
  useEffect(() => {
    const el = audioRef.current;
    return () => {
      el?.pause();
    };
  }, []);

  const handleToggle = useCallback(() => {
    if (fileMissing) return;
    let nextMuted = muted;
    let nextAllowed = userAllowedPlay;
    if (!userAllowedPlay) {
      // First tap — unlock per autoplay policy. Default unmuted unless reduced-motion
      // prefers silence (then unlock requires a second explicit tap).
      nextAllowed = true;
      nextMuted = !!reduced;
    } else {
      nextMuted = !muted;
    }
    setUserAllowedPlay(nextAllowed);
    setMuted(nextMuted);
    setAnnounce(nextMuted || !nextAllowed ? "Ambient audio off" : "Ambient audio on");
    onToggle?.({ muted: nextMuted, userAllowedPlay: nextAllowed });
  }, [fileMissing, muted, userAllowedPlay, reduced, onToggle]);

  /** Keyboard: M toggles mute while overlay is open. */
  useEffect(() => {
    if (!active || fileMissing) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key.toLowerCase() !== "m") return;
      // Don't fight typing in form fields (none expected, but defensive).
      const target = e.target as HTMLElement | null;
      if (target && (target.tagName === "INPUT" || target.tagName === "TEXTAREA")) {
        return;
      }
      e.preventDefault();
      handleToggle();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [active, fileMissing, handleToggle]);

  if (!active) return null;

  const src = `/audio/niche/mood-${mood}.mp3`;
  const isAudible = userAllowedPlay && !muted && !fileMissing;
  const Icon = isAudible ? Volume2 : VolumeX;
  const label = isAudible ? "Mute ambient audio" : "Unmute ambient audio";

  return (
    <>
      <audio
        ref={audioRef}
        src={src}
        loop
        preload="metadata"
        onError={() => setFileMissing(true)}
        aria-hidden="true"
      />
      {/* Polite live region — announces toggle state to AT users. */}
      <div aria-live="polite" aria-atomic="true" className="sr-only">
        {announce}
      </div>
      {!fileMissing && (
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            handleToggle();
          }}
          aria-label={label}
          aria-keyshortcuts="m"
          aria-pressed={isAudible}
          className="fixed bottom-[calc(1.5rem+var(--spacing-safe-bottom))] right-[calc(1.5rem+var(--spacing-safe-right))] z-[60] inline-flex size-10 items-center justify-center rounded-full border border-white/15 bg-black/45 text-white/85 backdrop-blur transition-colors hover:bg-black/70 focus:outline-none focus-visible:ring-2 focus-visible:ring-amber-400/70"
        >
          <Icon className="size-4" aria-hidden />
        </button>
      )}
    </>
  );
}
